import React, { useMemo } from 'react';

import { Box, Dialog, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';

import CreateInfiniteAccountTab from '../InfiniteConcierge/CreateInfiniteAccountTab';
import { InfiniteEnrolmentLaunchIntent } from '../InfiniteConcierge/createInfiniteAccountShared';

import { InfiniteApplicationEnrolmentRequest } from './ReviewApplicationDialog';

function getPhone(request: InfiniteApplicationEnrolmentRequest): string {
  return [request.applicant.phonePrefix, request.applicant.phone].filter(Boolean).join(' ');
}

function getAddress(request: InfiniteApplicationEnrolmentRequest): string {
  const applicant = request.applicant;
  return [applicant.address, applicant.address2, applicant.city, applicant.state, applicant.postcode, applicant.country]
    .filter(Boolean)
    .join(', ');
}

export default function InfiniteApplicationEnrolmentDialog({
  request,
  onClose,
  onCompleted,
}: {
  request: InfiniteApplicationEnrolmentRequest | null;
  onClose: () => void;
  onCompleted?: (request: InfiniteApplicationEnrolmentRequest) => void;
}) {
  const launchIntent = useMemo<InfiniteEnrolmentLaunchIntent | undefined>(() => {
    if (!request) {
      return undefined;
    }

    return {
      id: Date.now(),
      path: request.path,
      applicant: {
        firstName: request.applicant.firstName,
        lastName: request.applicant.lastName,
        email: request.applicant.email,
        phonePrefix: request.applicant.phonePrefix,
        phone: request.applicant.phone,
      },
    };
  }, [request]);

  const closeAfterOutcome = () => {
    if (request) {
      onCompleted?.(request);
    }
    onClose();
  };

  return (
    <Dialog open={Boolean(request)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{request?.path === 'new' ? 'Create new Infinite account' : 'Upgrade user to Infinite'}</DialogTitle>
      <DialogContent dividers>
        {request && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Application applicant
              </Typography>
              <Stack spacing={0.5}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {request.applicant.firstName} {request.applicant.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {request.applicant.role}
                  {request.applicant.relationship ? ` · ${request.applicant.relationship}` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {request.applicant.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getPhone(request)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getAddress(request) || '-'}
                </Typography>
              </Stack>
            </Box>

            <Divider />

            <CreateInfiniteAccountTab
              launchIntent={launchIntent}
              onBack={onClose}
              onCloseAfterOutcome={closeAfterOutcome}
            />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
